/**
 * Date/Time utilities
 */

import moment from 'moment-timezone';


export const isDateObject = (obj) => {
  return Object.prototype.toString.call(obj) === '[object Date]';
};

export const isValidDateObject = obj => {
  return isDateObject(obj) && !isNaN(obj.getTime());
};

export const castToDate = (obj, defaultTimezone = null) => {
  if (!obj) {
    return null;
  }

  if (isValidDateObject(obj)) {
    return obj;
  } else if (isDateObject(obj)) {
    // Invalid date object
    return null;
  }

  let dateObject;
  try {
    let parsedMoment;
    if (defaultTimezone) {
      parsedMoment = moment.tz(obj, defaultTimezone);
    } else {
      parsedMoment = moment(obj);
    }
    if (parsedMoment) {
      dateObject = parsedMoment.toDate();
    }
  } catch (e) {
    console && console.warn && console.warn("castToDate() error:", e, "attempting to cast:", obj);
    dateObject = null;
  }
  return dateObject;
};

export const castToDateWithoutTime = (obj, defaultTimezone = null) => {
  let dateObj = castToDate(obj, defaultTimezone);
  if (dateObj) {
    const hasTimePart = dateObj.getHours() !== 0
      || dateObj.getMinutes() !== 0
      || dateObj.getSeconds() !== 0
      || dateObj.getMilliseconds() !== 0;
    if (hasTimePart) {
      // Copy into a new object
      const newDateObj = new Date(dateObj.getTime());
      newDateObj.setHours(0);
      newDateObj.setMinutes(0);
      newDateObj.setSeconds(0);
      newDateObj.setMilliseconds(0);
      return newDateObj;
    }
  }
  return dateObj;
};

export const castToDateWithoutSeconds = (obj, defaultTimezone = null) => {
  let dateObj = castToDate(obj, defaultTimezone);
  if (dateObj) {
    if (dateObj.getSeconds() !== 0 && dateObj.getMilliseconds() !== 0) {
      // Copy into a new object
      const newDateObj = new Date(dateObj.getTime());
      newDateObj.setSeconds(0);
      newDateObj.setMilliseconds(0);
      return newDateObj;
    }
  }
  return dateObj;
};

export const switchDateToTimezone = (obj, targetTimezone, defaultTimezone = null) => {
  let dateObj = castToDate(obj, defaultTimezone);
  let converted = null;
  if (isValidDateObject(dateObj)) {
    // Use moment-timezone to do conversion
    let m = moment(dateObj);
    let inputUTCOffsetMinutes = m.utcOffset();

    // First update timezone, which shifts time accordingly
    m.tz(targetTimezone);

    // Then shift the time back
    let targetUTCOffsetMinutes = m.utcOffset();
    let minutesToShift = targetUTCOffsetMinutes - inputUTCOffsetMinutes;
    m.add(minutesToShift);
  }
  return converted;
};

export const formatAsDateString = (obj, defaultTimezone = null) => {
  const date = castToDate(obj, defaultTimezone);
  if (!date) {
    return null;
  }

  const m = moment(date);
  const dateString = m.format("YYYY-MM-DD");

  // const isoString = date.toISOString();
  // const dateString = isoString.slice(0, 10); // YYYY-MM-DD
  // // const dateString = date.toDateString();

  return dateString;
};

export const formatAsDateTimeStringWithTimezone = (obj, defaultTimezone) => {
  const date = castToDate(obj, defaultTimezone);
  const m = moment(date);
  const dateTimeString = m.format("YYYY-MM-DD h:mm a z");
  return dateTimeString;
};

export const formatAsDateTimeStringWithoutTimezone = (obj, useAmPm = false) => {
  const date = castToDate(obj);
  const isoString = date.toISOString();
  const dateString = isoString.slice(0, 10); // YYYY-MM-DD
  const timeString = formatAsTimeString(date, useAmPm);
  const dateTimeString = `${dateString} ${timeString}`;
  return dateTimeString;
};

export const formatHMSAsTimeString = (h, mm, ss, useAmPm = false) => {
  h = parseInt(h || 0);
  mm = parseInt(mm || 0);
  ss = parseInt(ss || 0);
  mm += Math.floor(ss / 60);
  ss = ss % 60;
  h += Math.floor(mm / 60);
  mm = mm % 60;

  ss = ss.toString();
  if (ss.length == 1) ss = `0${ss}`;

  mm = mm.toString();
  if (mm.length == 1) mm = `0${mm}`;

  let amPmSuffix = '';
  if (useAmPm) {
    if (h <= 11) {
      amPmSuffix = ' AM';
    } else if (h > 11 && h < 24) {
      amPmSuffix = ' PM';
      if (h > 12) {
        h -= 12;
      }
    }
  }

  let secondsPart = ss === '00' ? '' : `:${ss}`;

  const timeString = `${h}:${mm}${secondsPart}${amPmSuffix}`;
  return timeString;
};

export const formatAsTimeString = (obj, useAmPm = false) => {
  let timeString = null;
  if (isValidDateObject(obj)) {
    let h = obj.getHours();
    let mm = obj.getMinutes();
    let ss = obj.getSeconds();
    timeString = formatHMSAsTimeString(h, mm, ss, useAmPm);
  } else {
    /**
     * Match following time formats:
     *
     *  HH:MM
     *  HH:MM:SS
     *
     *  Allow for AM/PM identifiers at end of string
     */
    let str;
    if (obj && obj.toString) {
      str = obj.toString();
      const timeStringMatch= str.match(/(\d+):(\d+):?(\d+)?\s*(am|pm|a|p)?\s*$/i);
      if (timeStringMatch) {
        let h = timeStringMatch[1];
        let mm = timeStringMatch[2];
        let ss = timeStringMatch[3];
        let isPM = timeStringMatch[4] && timeStringMatch[4][0].toLowerCase() === 'p';
        if (isPM) {
          h = parseInt(h);
          if (h < 12) {
            h += 12;
          }
        }
        timeString = formatHMSAsTimeString(h, mm, ss, useAmPm);
      }
    }
  }
  return timeString;
};

export const convertToTimeInSeconds = obj => {
  let timeString = formatAsTimeString(obj);
  if (!timeString) return null;
  let result = timeString.match(/^(\d+):(\d+):(\d+)$/);
  if (result) {
    let secs = (parseInt(result[1]) * 60 * 60)
      + (parseInt(result[2]) * 60)
      + (parseInt(result[3]));
    return secs;
  }
  return null;
};

export const dateTimeStringIncludesTimeZone = (dateTimeString) => {
  const tzPatterns = [
    /.*Z$/,
    /.+[+-]\d{4}$/,
    /.+[+-]\d{2}:\d{2}$/,
  ];
  return tzPatterns.some(pattern => pattern.test(dateTimeString));
};

export const durationInHours = (date1, date2) => {
  if (isDateObject(date1) && isDateObject(date2)) {
    let t1 = date1.getTime();
    let t2 = date2.getTime();
    let secs = Math.abs(parseFloat(t1) - parseFloat(t2)) / 1000.0;
    let hrs = secs / 3600.0;
    return hrs;
  } else {
    return undefined;
  }
};

export const formattedDurationInHours = (date1, date2) => {
  let hrs = durationInHours(date1, date2);
  let duration = '';
  if (hrs !== undefined) {
    hrs = (hrs + (24 * 3)) % 24;
    duration = +(hrs.toFixed(2));
  }
  return duration;
};

export const getTimeDiffString = (date, now = null) => {
  let days, hrs, mins, secs, time;
  let timeDiffString = '';
  if (!now) {
    now = new Date();
  }
  if (date && date.getTime) {
    time = date.getTime();
  } else {
    time = date;
  }

  secs = (now.getTime() - time) / 1000.0;
  let isFuture = (secs < 0);
  secs = Math.abs(secs);

  if (secs > 60) {
    mins = Math.floor(secs / 60.0);
    secs = secs % 60;
  }
  if (mins > 60) {
    hrs = Math.floor(mins / 60.0);
    mins = Math.floor(mins) % 60;
  }
  if (hrs > 24) {
    days = Math.floor(hrs / 24.0);
    hrs = Math.floor(hrs) % 24;
  }
  if (days) {
    timeDiffString += ` ${days} day${days !== 1 ? 's' : ''}`;
  }
  if (days || hrs) {
    timeDiffString += ` ${hrs} hour${hrs !== 1 ? 's' : ''}`;
  }
  if (days || hrs || mins) {
    timeDiffString += ` ${mins} minute${mins !== 1 ? 's' : ''}`;
  }
  secs = Math.floor(secs);
  timeDiffString += ` ${secs} second${secs !== 1 ? 's' : ''}`;
  timeDiffString = timeDiffString.trim();
  if (isFuture) {
    timeDiffString += ' from now';
  } else {
    timeDiffString += ' ago';
  }
  return timeDiffString;
};

export function datesAreEqual(d1, d2) {
  return moment(d1).isSame(d2);
}
export function dateIsAfter(d1, d2) {
  return moment(d1).isAfter(d2);
}
export function dateIsBefore(d1, d2) {
  return moment(d1).isBefore(d2);
}


/**
 * The shapes the staff API sends program dates and recap availability
 * timestamps in. Parsing against an explicit list keeps moment off the JS
 * engine's own Date parser, which is what makes "YYYY-MM-DD HH:mm:ss" fail on
 * Safari and iOS and silently drop the bound built from it.
 *
 * Plain format strings rather than moment.ISO_8601: that sentinel is compared
 * by identity, so it only works when the caller's moment is the same module
 * instance as this one, and a foreign copy throws rather than degrading.
 */
export const PROGRAM_DATE_FORMATS = [
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ssZ',
];

export const RECAP_TIMESTAMP_FORMATS = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ssZ',
];

/**
 * One end of a date window, taken from whichever candidates actually parsed.
 *
 * Both ways of doing this by hand fail the same way: moment.max()/moment.min()
 * propagate an invalid moment, and Math.max()/Math.min() over Date objects
 * propagate its NaN. The result is a bound that no comparison is ever true
 * against, so the window silently stops being enforced — reachable through a
 * program with no start or end date, and through the "YYYY-MM-DD HH:mm:ss"
 * recap timestamps that Safari and iOS refuse to parse.
 *
 * @param {function} pick moment.max for a lower bound, moment.min for an upper
 * @param {Array} candidates moments, valid or not
 *
 * @returns {?Date} null when nothing parsed, meaning that end is unbounded
 */
export const boundFromValidDates = (pick, candidates) => {
  const usable = candidates.filter((candidate) => candidate && candidate.isValid());

  return usable.length ? pick(usable).toDate() : null;
};

/**
 * Minutes as a readable duration, for naming a configured limit in a message:
 * 30 -> '30 minutes', 60 -> '1 hour', 117 -> '1 hour 57 minutes'.
 *
 * Hand-rolled because the alternatives are wrong here, not for lack of a
 * library: moment's humanize() rounds (117 minutes reads as "2 hours"), and
 * duration.hours() wraps at a day (1500 minutes reads as "1 hour"). A limit has
 * to be reported exactly. Matches date-fns formatDuration, which Staff App 2.0
 * uses for the same messages.
 */
export const formatMinutesDuration = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (remainder > 0 || !parts.length) {
    parts.push(`${remainder} ${remainder === 1 ? 'minute' : 'minutes'}`);
  }

  return parts.join(' ');
};

// Timestamp for today at an "HH:mm" clock time, or null when the value is not
// that shape or the hour (0-23) or minute (0-59) is out of range; setHours would
// otherwise roll "99:99" over into some other time.
export const todayAtClockTime = (value) => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '');
  if (!match) {
    return null;
  }
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  return hour <= 23 && minute <= 59 ? new Date().setHours(hour, minute, 0, 0) : null;
};
